import md5 from 'blueimp-md5';
import jwtDecode from 'jwt-decode';
import { history } from 'umi';

import { authLogout, authRefreshToken } from '@/services/login';
import tokenManager from '@/services/token';
import { setAuthority } from '../auth';
import { authRedirect } from '../utilities';
import { getToken, isPublicFragment, removeTokenById } from './tokenManager';

let notifyContext = null;
export const setNotifyContext = (context) => (notifyContext = context);

// Mutex for preventing concurrent token refresh requests
let refreshPromise = null;
let isRefreshing = false;

export const cleanupLoginData = (useRedirect = false) => {
  const authKeys = [
    'x-auth-token',
    'antd-pro-authority',
    'x-auth-user',
    'x-userContext',
    'x-auth-token-refresh',
    'x-auth-refreshing',
  ];

  authKeys.forEach((key) => localStorage.removeItem(key));
  sessionStorage.clear();

  let loginUrl = '/user/login';
  let portal = localStorage.getItem('x-auth-companyPortalConfig');

  const currentPath = history.location?.pathname;
  const isLoginUrl = currentPath === '/user/login';
  const queryParams = [];

  if (portal) {
    try {
      portal = JSON.parse(portal);
      queryParams.push(`portal=${portal.id}`);
    } catch (e) {}
  }

  if (useRedirect && currentPath !== '/') {
    const redirect = isLoginUrl
      ? new URLSearchParams(window.location.search).get('redirect')
      : window.location.href;

    if (redirect) {
      queryParams.push(`redirect=${encodeURIComponent(redirect)}`);
    }
  }

  if (queryParams.length > 0) {
    loginUrl += `?${queryParams.join('&')}`;
  }

  history.replace(loginUrl, history.location?.state || portal);
};

export const performLogout = async (useRedirect = false) => {
  localStorage.setItem('x-logout-in-progress', 'true');
  try {
    await authLogout();
  } finally {
    cleanupLoginData(useRedirect);
  }
};

/**
 * Safe logout function that checks logout flag to prevent concurrent logouts
 * @param {boolean} useRedirect - Whether to redirect after logout
 */
export const safeLogout = async (useRedirect = false) => {
  if (!localStorage.getItem('x-logout-in-progress')) {
    try {
      await performLogout(useRedirect);
    } catch (e) {}
  }
};

export const handlePublicFragmentAuth = () => {
  const query = history.location?.query || {};
  const { publicCode, code } = query;

  const type = publicCode ? 'public' : 'external';
  const id = publicCode ?? md5(code);

  removeTokenById(type, id);
  window.location.reload();
};

export const handleTokenRefresh = async (token) => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = performTokenRefresh(token);
  try {
    const result = await refreshPromise;
    if (result) {
      const { getQueueLength, processRequestQueue } = require('./requestQueue');
      if (getQueueLength() > 0) processRequestQueue();
    }
    return result;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
};

const performTokenRefresh = async (token) => {
  try {
    const existingRefresh = localStorage.getItem('x-auth-refreshing');
    if (existingRefresh) {
      const refreshTime = parseInt(existingRefresh);
      // Assume success, the other refresh will handle it
      if (Date.now() - refreshTime < 30_000) return true;
    }

    localStorage.setItem('x-auth-refreshing', Date.now().toString());
    window.dispatch?.({ type: 'global/willRefreshToken' });

    const refreshedToken = await authRefreshToken(token);
    localStorage.removeItem('x-userContext');

    if (refreshedToken?.accessToken) {
      const user = jwtDecode(refreshedToken.accessToken);

      localStorage.setItem('x-auth-token', JSON.stringify(refreshedToken));
      localStorage.setItem('x-auth-token-refresh', 'true');

      window.dispatch?.({ type: 'userContext/defaultContext', payload: user });
      setAuthority(user.role);
      // authRedirect(refreshedToken);
      // Sync token refresh with SocketService via token events
      try {
        const expiresAt = Date.now() + (refreshedToken.expiresIn || 0) * 1000;
        await tokenManager.updateToken(user.id, refreshedToken.accessToken, expiresAt);
        // Proactively request socket reconnection so new token is applied immediately
        tokenManager.requestSocketReconnect('token_refreshed');
      } catch (e) {}

      localStorage.removeItem('x-auth-refreshing');
      window.dispatch?.({ type: 'global/resetRefreshToken' });
      return true;
    }

    localStorage.removeItem('x-auth-refreshing');
    window.dispatch?.({ type: 'global/resetRefreshToken' });
    const { clearRequestQueue } = require('./requestQueue');
    clearRequestQueue(new Error('Token refresh failed'));
    return false;
  } catch (e) {
    localStorage.removeItem('x-auth-refreshing');
    window.dispatch?.({ type: 'global/resetRefreshToken' });
    const { clearRequestQueue } = require('./requestQueue');
    clearRequestQueue(e);
    return false;
  } finally {
    localStorage.removeItem('x-auth-refreshing');
  }
};

/**
 * Handle 401/403 authentication errors
 * @param {Response} response - HTTP response object
 * @param {AbortController} abortController - Abort controller
 */
export const handleAuthError = async (response, abortController) => {
  abortController.abort();
  const token = getToken();

  if (response.status === 403) {
    history.replace('/exception/403');
    return;
  }

  if (
    response.url.includes('/refreshToken') ||
    (response.status === 401 && response.url.includes('/logout'))
  ) {
    cleanupLoginData(true);
    return;
  }

  if (token) {
    if (isPublicFragment()) {
      handlePublicFragmentAuth();
    } else if (token.refreshToken) {
      notifyContext?.notify?.('tokenRefresh');
      const refreshSuccess = await handleTokenRefresh(token);
      if (!refreshSuccess) await performLogout(true);
    } else await performLogout(true);
  }
};
